<template>
  <ab-flow-base-cmp v-if="isReady" :block="block" :class="classesString" :style="stylesString">
    <q-card flat class="chat-card column no-wrap" :class="{'opacity-0': !isScrolledToEnd}">
      <q-card-section style="flex-basis: 100%;" class="q-pa-none">
        <q-scroll-area ref="scrollArea" class="full-height" scroll-position="end">
          <div ref="messagesContainer" class="q-px-sm q-pb-md">
            <div v-if="visiblePaginationBtn" class="text-center">
              <button class="btn-previews-messages" type="button" @click="loadPreviewsMessages">Load previous messages</button>
            </div>

            <div v-for="(item, idx) in historyStacked" :key="item.id">
              <template v-if="item.widget">
                <div class="row full-width" :class="{'justify-end': item.type === 'outgoing'}">
                  <div>
                    <diagram-component-editor-cmp
                        ref="diagram"
                        :block="item.widget.block"
                        :arguments="item.widget.arguments"
                    />
                  </div>
                </div>
              </template>
              <template v-else>

                <template v-if="['image'].includes(item?.metadata?.type)">

                  <q-chat-message
                      class="chat-message attachment"
                      :stamp="item.sent_at"
                      :sent="item.type === 'outgoing'"
                      :style="item.type === 'outgoing' ? outgoingStyles : incomingStyles"
                      text-html
                  >
                    <template #default>
                      <chat-media-attachment :title="item?.message" :type="item?.metadata?.type" :media-url="item?.metadata?.mediaUrl" />
                    </template>
                  </q-chat-message>

                </template>

                <q-chat-message v-else
                                class="chat-message"
                                :text="Array.isArray(item.message) ? item.message : [item.message]"
                                :stamp="item.sent_at"
                                :sent="item.type === 'outgoing'"
                                :style="item.type === 'outgoing' ? outgoingStyles : incomingStyles"
                                text-html
                                :size="messageSize"
                                @click="onContentClick(item)"
                >
                  <template v-if="item.options && item.options.length" #default>
                    <div v-for="(msg, idx) in Array.isArray(item.message) ? item.message : [item.message]" :key="idx">
                      <div v-html="msg" />

                      <template v-if="idx === [item.message].flat().length - 1">
                        <div class="row full-width dg-gutter-x-sm">
                          <q-btn
                            v-for="option in item.options"
                            :key="option.event"
                            class="chat-opt-btn q-mt-sm dg-radius-br-sm dg-radius-bl-sm dg-radius-tr-sm dg-radius-tl-sm dg-text-weight-regular"
                            :class="optBtnClasses"
                            unelevated
                            :label="option.text"
                            @click="sendAnswer(option)"
                          />
                        </div>

                        <q-separator spaced />
                      </template>
                    </div>
                  </template>

                  <template #name>
                    {{ item.type === 'incoming' ? botName : userName }}
                    <q-spinner-dots
                        v-if="hasPreloader && isLastMessageIncoming && idx === historyStacked.length - 1"
                        size="1rem"
                    />
                  </template>
                </q-chat-message>
              </template>
            </div>

            <q-chat-message
              v-if="!isLastMessageIncoming && !forceHidePreloader"
              class="chat-message"
              :name="botName"
              :style="incomingStyles"
            ><q-spinner-dots size="2rem" /></q-chat-message>
          </div>
        </q-scroll-area>
      </q-card-section>

      <q-card-section v-if="!readonly" class="q-pa-none">
        <q-input
            class="chat-field"
            v-model="message"
            filled
            autogrow
            placeholder="Type your message here..."
            :style="fieldStyles"
        >
          <template #append>
            <q-btn
                :class="btnClass"
                round
                dense
                flat
                icon="send"
                :disable="!this.message.length"
                :loading="isBusy"
                v-on="btnEvents"
            />
          </template>
        </q-input>
      </q-card-section>
    </q-card>
  </ab-flow-base-cmp>
</template>

<script>
import {computed, reactive} from 'vue';
import moment from 'moment';
import {AbStorage} from 'a2u-renderer-common/src/utils/abStorage';
import AbFlowBaseCmp from "../Containers/Designer/AbFlowBaseCmp";
import {renderMixins} from "../renderMixins";
import DiagramComponentEditorCmp from '../Logic/DiagramComponent/DiagramComponentEditorCmp.vue';
import ChatMediaAttachment from "./ChatMediaAttachment.vue";
import {ChatProcessor} from "./ChatProcessor.js";

/**
 * Checks if the gap between two moments is larger than the specified maximum gap in minutes.
 *
 * @param {string|Date|moment.Moment} start - The start time.
 * @param {string|Date|moment.Moment} end - The end time.
 * @param {number} maxGapMinutes - The maximum allowed gap in minutes.
 * @returns {boolean} True if the gap is larger than the specified maximum gap, false otherwise.
 */
function isGapTooLarge(start, end, maxGapMinutes) {
  const gap = moment.unix(end).diff(moment.unix(start));
  return gap > maxGapMinutes * 60 * 1000;
}

/**
 * Safely parses a JSON string.
 *
 * @param {string} str - The JSON string to parse.
 * @returns {Object|null} The parsed object, or null if parsing fails.
 */
const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
};

export default {
  components: {ChatMediaAttachment, DiagramComponentEditorCmp, AbFlowBaseCmp},
  mixins: [renderMixins],
  props: ['block'],
  name: "ChatEditorCmp",

  // Data properties of the component
  data() {
    return {
      isReady: false, // Indicates if the component is ready
      isBusy: false, // Indicates if the component is busy
      message: '', // The message to be sent
      dbModel: null, // The database model
      storageData: false,
      scrollTimeout: undefined,
      isScrolledToEnd: false,
      pagination: {
        perPage: 50,
        page: 1,
        newMessages: 0,
        active: false,
      },
      containerHeight: 0,
      containerResizeObserver: null,
      forceHidePreloaderTimeout: null,
      forceHidePreloader: true,
      btnEvents: {}
    };
  },

  // Computed properties of the component
  computed: {
    // Returns the channel of the block or 'chat' if not defined
    channel() {
      return this.interpretString(this.block?.properties?.channel);
    },

    /**
     * Get the chat bot id
     * @return {*}
     */
    abBotId() {
      return this.interpretString(this.block?.properties?.abChatBotId);
    },

    // Returns the diagramId of the block
    diagramId() {
      return this.block?.properties?.diagramId;
    },

    // Returns the chatId
    chatId() {
      return `chat:${this.channel}`;
    },

    // Returns the total number of messages in the chat
    totalMessages() {
      return this.wait('totalMessages', async () => {
        return (await this.dbModel?.query(['count(1) cnt'])?.where({chat_id: this.channel}).firstRaw())?.cnt;
      }, 0);
    },

    // Returns the raw history of the chat
    historyRaw() {
      const timeZoneOffset = (new Date().getTimezoneOffset() / 60);

      return this.wait('historyRaw', async () => {
        const { page, perPage, newMessages } = this.pagination;

        const limit = (perPage * Math.max(1, page)) + newMessages;
        const order = `CASE
              WHEN sent_at LIKE '%-%' THEN CAST(strftime('%s', sent_at, '${timeZoneOffset} hours') AS INT)
              ELSE CAST(sent_at AS INT)
          END`;

        const sqlRaw = `
          SELECT * FROM (
              SELECT *
              FROM ${this.dbModel?.entity}
              WHERE chat_id = ?
              ORDER BY ${order} DESC
              LIMIT ${limit}
          ) subquery ORDER BY ${order} ASC
        `;

        return (await this.dbModel?.query()?.getRaw({
          query: sqlRaw,
          params: [this.channel],
        })).map((v) => {
          if (v?.options && typeof v.options === 'string') {
            v.options = safeJsonParse(v.options);
          }
          if (v?.metadata && typeof v.metadata === 'string') {
            v.metadata = safeJsonParse(v.metadata);
          }

          return v;
        });
      }, []);
    },

    // Returns the history of the chat with parsed messages and relative timestamps
    history() {
      const marked = this.renderer?.a2u?.device?.getPlugin('MarkdownParser');

      return this.historyRaw.map((item) => ({
        ...item,
        message: marked && item.message ? marked.parse(item.message) : item.message,
        // todo: Remove after a certain period. Added on 04-09-2024.
        sent_at: (/^\d+$/.test(item.sent_at) ? moment.unix(item.sent_at) : moment(item.sent_at)).format('YYYY-MM-DD HH:mm:ss'),
        image: item?.metadata?.type === 'image' ? item.message : null,
        widget: item?.metadata?.widgetId || item?.metadata?.widget ? this.getWidgetBlock(item?.metadata?.widgetId || item?.metadata?.widget, item) : null,
      }));
    },

    // Returns the stacked history of the chat
    historyStacked() {
      const result = [];
      let group = [];
      let lastMessageTime = null;

      // Group messages by type and time
      for (const message of this.history) {
        if (group.length === 0) {
          group.push(message);
          lastMessageTime = message.sent_at;
        } else {
          const lastMessage = group[group.length - 1];

          // If the message type is the same as the last message type and the gap is not too large
          if (
              message.type === lastMessage.type &&
              !isGapTooLarge(lastMessageTime, message.sent_at, 1) &&
              !lastMessage.widget &&
              !lastMessage.metadata?.type &&
              !message.metadata?.type &&
              !message.widget) {
            group.push(message);
            lastMessageTime = message.sent_at;
          } else {
            result.push(group);
            group = [message];
            lastMessageTime = message.sent_at;
          }
        }
      }

      // Add the last group
      if (group.length > 0) {
        result.push(group);
      }

      /*console.warn(result.map((stack) => {
        if (stack.length === 1) {
          return stack[0];
        }

        return {
          ...stack[stack.length - 1],
          message: stack.map((s) => s.message),
        };
      }))*/

      return result.map((stack) => {
        if (stack.length === 1) {
          return stack[0];
        }

        return {
          ...stack[stack.length - 1],
          id: stack[0].id,
          message: stack.map((s) => s.message),
        };
      });
    },

    // Returns the length of the history
    historyLength() {
      return this.history.length;
    },

    // Returns the styles for the outgoing messages
    outgoingStyles() {
      return {
        '--message-text-color': `var(--foreground-color-${this.block?.properties?.outgoingTextColor || 'light'})`,
        '--message-bg-color': `var(--background-color-${this.block?.properties?.outgoingBgColor || 'info'})`,
      };
    },

    // Returns the styles for the incoming messages
    incomingStyles() {
      return {
        '--message-text-color': `var(--foreground-color-${this.block?.properties?.incomingTextColor || 'light'})`,
        '--message-bg-color': `var(--background-color-${this.block?.properties?.incomingBgColor || 'success'})`,
      };
    },

    // Returns the styles for the field
    fieldStyles() {
      return {
        '--chat-control-text-color': `var(--foreground-color-${this.block?.properties?.fieldTextColor || 'dark'})`,
        '--chat-control-bg-color': `var(--background-color-${this.block?.properties?.fieldBgColor || 'tertiary'})`,
      };
    },

    // Returns the class for the button
    btnClass() {
      return `dg-foreground-${this.block?.properties?.btnTextColor || 'dark'}`;
    },

    storage() {

      // Create storage
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      if (!this.storageData) this.storageData = new AbStorage({
        initialStorage: this.renderer.a2u.source?.storage?.data?.[`diagram-${this.diagramId}`],
        storageCreator: (data) => reactive(data),
      });

      // Set incoming params
      for (const [name, val] of Object.entries(this.getArguments(this.block.id, this.block.properties.arguments) || {})) {
        if (val !== undefined) this.storageData.set(name, val)
      }

      // Return storage
      return computed(() => this.storageData);
    },

    // Returns the keyboard visibility flag
    keyboardVisibility() {
      return this.renderer.a2u.storage.get('settings.application.isKeyboardOpen', false);
    },

    // Read-only flag
    readonly() {
      return this.getValue(this.block?.properties?.readonly) || false;
    },

    // Returns the bot name
    botName() {
      return this.interpretString(this.block?.properties?.botName) || 'bot';
    },

    // Returns the user name
    userName() {
      return this.interpretString(this.block?.properties?.userName) || 'me';
    },

    /**
     * Computed property that returns the arguments for the widget.
     * It iterates over the widgetArgs property of the block (or an empty array if not defined),
     * and for each argument, it adds a new property to the resulting object with the name of the argument as the key
     * and the evaluated value of the argument as the value.
     * @return {Object} An object containing the arguments for the widget.
     */
    widgetArgs() {
      return (this.block?.properties?.widgetArgs || []).reduce((args, arg) => {
        args[arg.name] = this.getValue(arg.value);

        return args;
      }, {});
    },

    /**
     * Computed property that checks if the `scrollAnimated` property is defined and equals 1 in the block's properties.
     * If it is, it returns true.
     * If it is not, it returns false.
     * This property can be used to determine if scroll animation is enabled.
     * @return {boolean} True if scroll animation is enabled, false otherwise.
     */
    scrollAnimated() {
      return this?.block?.properties?.scrollAnimated === 1;
    },

    /**
     * Computed property that returns the duration for the scroll animation in the chat.
     * It first checks if the `scrollDuration` property is defined in the block's properties.
     * If it is, it returns the value of the `scrollDuration` property.
     * If it is not, it returns a default value of 300.
     * @return {number} The duration for the scroll animation in the chat.
     */
    scrollDuration() {
      return this?.block?.properties?.scrollDuration || 300;
    },

    /**
     * Checks if the `withChatDiagram` property is defined and equals 1 in the block's properties.
     *
     * This computed property can be used to determine if the chat diagram is enabled.
     *
     * @return {boolean} True if the chat diagram is enabled, false otherwise.
     */
    withChatDiagram() {
      return this.block?.properties?.withChatDiagram === 1;
    },

    /**
     * Checks if the bot is currently writing.
     *
     * @returns {boolean} True if the bot is writing, otherwise false.
     */
    hasPreloader() {
      return (ChatProcessor.currentChatStatuses[this.channel] === 'typing') || !!this.getValue(this.block?.properties?.botIsWriting);
    },

    /**
     * Returns the message size.
     *
     * @returns {string} The message size from the block properties, or 9 if not defined.
     */
    messageSize() {
      return String(this.block?.properties?.messageSize || 9);
    },

    /**
     * Checks if the last message in the chat history is incoming.
     *
     * @returns {boolean} True if the last message is incoming, false otherwise.
     */
    isLastMessageIncoming() {
      return this.historyRaw[this.historyRaw.length - 1]?.type === 'incoming';
    },

    // Options button classes
    optBtnClasses() {
      return [
        `dg-foreground-${this.block?.properties?.optBtnTextColor || 'light'}`,
        `dg-background-${this.block?.properties?.optBtnBgColor || 'info'}`,
      ];
    },

    /**
     * Computes the number of visible messages based on the current pagination settings.
     *
     * @returns {number} The number of visible messages.
     */
    visibleMessagesCount() {
      return this.pagination.perPage * this.pagination.page;
    },

    /**
     * Determines if the pagination button should be visible based on the total number of messages.
     *
     * @returns {boolean} True if the pagination button should be visible, false otherwise.
     */
    visiblePaginationBtn() {
      return this.totalMessages > this.visibleMessagesCount;
    },
  },

  // Methods of the component
  methods: {
    /**
     * Lifecycle hook called when the component is mounted
     * @return {Promise<void>}
     */
    async initChat() {
      // Initialize the chat component
      try {
        await this.init();
      } catch (e) {
        console.error('Error while initializing chat:', e);

        return;
      }

      // Set the component as ready
      this.isReady = true;

      setTimeout(() => {
        this.scrollToEnd(true);

        this.$watch(() => this.totalMessages, async (newVal, oldVal) => {
          this.pagination.newMessages += newVal - oldVal;

          this.scrollToEnd();
        });

        this.containerResizeObserver = new ResizeObserver(this.onResize);
        this.containerResizeObserver.observe(this.$refs.messagesContainer);
      }, 100);
    },


    /**
     * This method is used to send an answer to the chatbot.
     * It constructs a payload object with the chatId and the answer provided.
     * Then, it checks if there is a promise associated with the chatId in the process manager.
     * If there is no promise, it runs the process with the diagramId and the payload.
     * If there is a promise, it resolves the promise with the payload.
     *
     * @async
     * @param {string} answer - The answer to be sent to the chatbot.
     * @param {string} type
     * @throws Will throw an error if the process manager encounters an issue while running or resolving the promise.
     */
    async sendToChatBot(answer, type = 'outgoing') {

      const isInitMessage = typeof answer === 'string' && answer === '' && type === 'outgoing';

      // Check answer type
      if (typeof answer === 'string') {
        answer = {
          type: 'text',
          content: answer,
        };
      }

      this.isBusy = true;
      try {

        // If the chat diagram is not enabled, return immediately
        if (this.withChatDiagram) {
          const payload = {
            chatId: this.chatId,
            answer,
            type,
          };

          if (!await this.renderer.a2u.processManager.hasPromise(this.chatId)) {
            this.renderer.a2u.processManager.run(this.parentDiagram, this.diagramId, payload, this.storage);
          } else {
            await this.renderer.a2u.processManager.resolvePromise(this.parentDiagram, this.chatId, payload, this.storage);
          }
        }

        if (this.block.properties?.connectedToBackend) {

          // Get chat arguments
          const chatArgs = this.getArguments(this.block.id, undefined, this.block.properties.chatArgs || {});

          // Send message to remote server
          const snRes = await this.renderer.app.client.call(this.renderer.a2u.getBackendUrl() + "/ab-chat", "sendMessage", this.abBotId, this.channel, answer, chatArgs);
          if (snRes !== true) {
            throw new Error(snRes);
          }
        }

        if (!isInitMessage) {
          // Notify the parent diagram about the message sent
          this.parentDiagram.processOutgoingLinks(this, this.block.id, {
            message: answer,
            type,
          }, 'message-sent');
        }

      } catch (e) {
        console.error('Error while sending answer:', e);
      } finally {
        this.isBusy = false;
      }
    },

    /**
     * This method is used to send an answer to the chatbot.
     * It first checks if the component is busy. If it is, it returns immediately.
     * If the component is not busy, it sets the isBusy flag to true and starts the process of sending the answer.
     * It first saves the answer to the database, then it saves the outgoing message to the database.
     * After that, it sends the answer to the chatbot using the sendToChatBot method.
     * If an error occurs during this process, it logs the error to the console.
     * Finally, it sets the isBusy flag back to false.
     *
     * @async
     * @param {string} option - The answer to be sent to the chatbot.
     * @throws Will log an error to the console if an error occurs during the process.
     */
    async sendAnswer(option) {
      if (this.isBusy) {
        return;
      }

      try {
        /*await this.dbModel.save({
          id: message.id,
          option: e,
        });

        await this.dbModel.save({
          chat_id: this.channel,
          type: 'outgoing',
          message: message.options.find((v) => v.value === e).label,
          options: [],
          option: e,
          sent_at: moment.utc().local().unix(),
        });*/

        switch (option.type) {
          case "answer":
            return await this.sendToChatBot(Object.assign({}, option, {type: "callback"}));
          case "ui-event":
            return this.parentDiagram.processOutgoingLinks(this, this.block.id, option.callbackId, option.event);
        }

      } catch (e) {
        console.error('Error while sending answer:', e);
      }
    },

    /**
     * This method is used to send a raw message to the chatbot.
     * It first checks if the component is busy. If it is, it returns immediately.
     * If the component is not busy, it sets the isBusy flag to true and starts the process of sending the message.
     * It first checks if the message is not empty. If it is, it returns immediately.
     * Then, it saves the outgoing message to the database.
     * After that, it sends the message to the chatbot using the sendToChatBot method.
     * It then clears the message input field and scrolls to the end of the chat.
     * If an error occurs during this process, it logs the error to the console.
     * Finally, it sets the isBusy flag back to false.
     *
     * @async
     * @throws Will log an error to the console if an error occurs during the process.
     */
    async sendRawMessage(e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.isBusy) {
        return;
      }

      try {
        if (!this.message) {
          return;
        }

        await this.dbModel.save({
          chat_id: this.channel,
          type: 'outgoing',
          message: this.message,
          sent_at: moment.utc().local().unix(),
        });

        await this.sendToChatBot(this.message);

        this.message = '';
      } catch (e) {
        console.error('Error while sending message:', e);
      }
    },

    /**
     * This method is used to listen to the chat completion event.
     * It sets the isBusy flag back to false.
     */
    async chatCompleteListener() {
      this.isBusy = false;
    },

    /**
     * This method is used to listen to new messages in the chat.
     * It first sets the isBusy flag back to false.
     * Then, it saves the incoming message to the database and scrolls to the end of the chat.
     *
     * @async
     * @param {object} message - The incoming message object.
     * @param {object} options - The options associated with the incoming message.
     * @param {string} date - The date the message was sent.
     */
    async chatNewMessageListener({message, options, date}) {
      // Save the incoming message to the database
      await this.dbModel.save({
        id: undefined,
        chat_id: this.channel,
        type: 'incoming',
        message: message,
        options: options || [],
        option: null,
        sent_at: moment.utc(date).local().unix(),
      });

      this.isBusy = false;
      this.scrollToEnd();
    },

    /**
     * This method is used to initialize the chat component.
     * It first checks if the channel and the diagramId are defined. If not, it logs an error and returns immediately.
     * Then, it retrieves the database associated with the block. If the database is not defined, it logs an error and returns immediately.
     * It then retrieves the tableId and the fields of the table from the database. If the table or its fields are not defined, it logs an error and returns immediately.
     * It checks if the table structure is compatible by checking if it contains all the required fields. If not, it logs an error and returns immediately.
     * It then sets the dbModel property to the model of the table in the database.
     * It sets up an event listener for new messages in the chat. When a new message is received, it saves the message to the database and scrolls to the end of the chat.
     * If there is no promise associated with the chatId in the process manager, it runs the process with the diagramId and the chatId.
     *
     * @async
     * @throws Will log an error to the console if an error occurs during the initialization process.
     */
    async init() {
      // Check if the channel are defined
      if (!this.channel) {
        console.error('Channel is not defined for chat:', this.block);
        return;
      }

      // Check if the diagramId is defined
      if (!this.diagramId && this.withChatDiagram) {
        console.error('Diagram is not defined for chat:', this.block);
        return;
      }

      // Retrieve the database
      const db = this.renderer?.a2u?.dbs[this.block?.properties?.dbId];

      // Check if the database is defined
      if (!db) {
        console.error('Database is not defined for chat:', this.block);
        return;
      }

      // Retrieve the tableId and the fields of the table
      const tableId = this.block?.properties?.tableId;

      // Set the dbModel property to the model of the table in the database
      this.dbModel = db?.models[tableId];

      // Check if the dbModel is defined
      if (!this.dbModel) {
        console.error('Table is not defined for chat:', this.block);
        return;
      }

      const tableFields = Object.keys(this.dbModel?.fields || {});

      // Check if the table or its fields are defined
      if (!tableFields) {
        console.error('Table is not defined for chat:', this.block);
        return;
      }

      // Check if the table structure is compatible
      const requiredFields = ['id', 'chat_id', 'type', 'message', 'sent_at'];
      if (!requiredFields.every((field) => tableFields.includes(field))) {
        console.error('Table structure is incompatible:', this.block);
        return;
      }

      // If the chat diagram is enabled
      if (this.withChatDiagram) {
        // Subscribe to the chat completion event
        this.renderer.a2u.processManager.eventEmitter.on(`chat:complete.${this.channel}`, this.chatCompleteListener);

        // Subscribe to new messages in the chat
        this.renderer.a2u.processManager.eventEmitter.on(`chat:new-message.${this.channel}`, this.chatNewMessageListener);

        // Run the process if there is no promise associated with the chatId in the process manager
        if (!await this.renderer.a2u.processManager.hasPromise(this.chatId)) {
          this.renderer.a2u.processManager.run(this.parentDiagram, this.diagramId, {
            chatId: this.chatId,
          }, this.storage);
        }
      }

      // Register management event
      this.parentDiagram.registerHandler(this.block.id, 'incomingEvent', this.incomingEventHandler)

      // check if remote server is connected
      if (this.block.properties?.connectedToBackend && this.block.properties?.initChat) {

        // Check if there is not history
        const currentMessCount = (await this.dbModel?.query(['count(1) cnt'])?.where({chat_id: this.channel}).firstRaw())?.cnt

        // Send init message if no history
        if (!currentMessCount) {
          await this.sendToChatBot("");
        }
      }
    },

    /**
     * Scrolls the chat to the end.
     * This method is used to ensure that the latest messages are visible in the chat.
     * It first gets the scroll area and the scroll target from the component's refs.
     * Then, it sets the scroll position of the scroll area to the scroll height of the scroll target.
     * This effectively scrolls the chat to the end.
     * The scroll animation can be performed with or without animation, depending on the `withoutAnimation` parameter.
     * By default, the scroll animation is performed with animation.
     * The duration of the scroll animation is determined by the `scrollDuration` computed property of the component.
     *
     * @param {boolean} [withoutAnimation=false] - A flag indicating whether the scroll animation should be performed without animation.
     */
    scrollToEnd(withoutAnimation = false) {
      clearTimeout(this.scrollTimeout);

      this.scrollTimeout = setTimeout(() => {
        const scrollArea = this.$refs.scrollArea;

        const duration = withoutAnimation || !this.scrollAnimated ? 0 : this.scrollDuration;

        scrollArea.setScrollPercentage('vertical', 1, duration);

        if (!this.isScrolledToEnd) {
          setTimeout(() => {
            this.isScrolledToEnd = true;
          }, duration);
        }
      }, 100);
    },

    /**
     * This method is used to handle incoming events.
     * @param event
     * @param data
     * @return {Promise<void>}
     */
    async incomingEventHandler({event, data}) {
      switch (event) {
        case 'clear-messages':
          await this.clearMessages();
          return;
        case "callback":
          await this.sendToChatBot(Object.assign({type: "callback", callbackId: data}));
          return;
      }
    },

    /**
     * This method is used to clear the messages in the chat.
     * @return {Promise<void>}
     */
    async clearMessages() {
      try {
        await this.dbModel.query().getRaw({
          query: `DELETE
                  FROM ${this.dbModel.entity}
                  WHERE chat_id = ?`,
          params: [this.channel],
        });

        this.dbModel.triggerUpdate();
      } catch (e) {
        console.error('Error clearing messages:', e);
      }
    },

    /**
     * This method is used to get the widget block.
     * @param alias / widget id
     * @param data
     * @return {Object}
     */
    getWidgetBlock(alias, data) {
      // Find the widget
      const widget = (this.renderer?.source?.diagrams || []).find((d) => (d.alias === alias || d.id === alias) && d.diagram_type === 'widget');

      if (!widget) {
        console.error(`Widget not found: ${alias}`);

        return undefined;
      }

      // Get the markdown parser
      const marked = this.renderer?.a2u?.device?.getPlugin('MarkdownParser');

      let message = '';

      if (data?.metadata?.type === 'image') {
        message = data?.metadata?.image || data.message;
      } else {
        message = marked && data.message ? marked.parse(data.message) : data.message
      }

      return {
        block: {
          widget: widget.title,
          type: "DiagramComponent",
          id: widget.id,
          properties: {
            diagramComponentId: widget.id,
          }
        },
        arguments: {
          ...this.widgetArgs,
          ...data,
          ...(data?.metadata?.widgetArgs || {}),
          message_type: data?.metadata?.type || 'text',
          message,
          sent_at: data.sent_at,
        },
      };
    },

    /**
     * This method is used to handle the content click event.
     * @param message
     */
    onContentClick(message) {
      this.parentDiagram.processOutgoingLinks(this, this.block.id, message, 'content-click');
    },

    /**
     * Loads the previous messages by incrementing the pagination page.
     */
    loadPreviewsMessages() {
      this.pagination.active = true;
      this.pagination.page++;
    },

    /**
     * Handles the resize event for the chat container.
     * Adjusts the scroll position to maintain the current view.
     *
     * @param {ResizeObserverEntry[]} entry - The resize observer entry.
     */
    onResize([entry]) {
      const {height} = entry.contentRect;
      const scrollPosition = this.$refs.scrollArea.getScrollPosition().top;

      if (this.pagination.active) {
        this.$refs.scrollArea.setScrollPosition('vertical', height - this.containerHeight + scrollPosition, 0);
      }

      this.containerHeight = entry.contentRect.height;

      this.pagination.active = false;
    },
  },

  watch: {
    /**
     * Watcher for the keyboardVisibility computed property.
     * When the keyboard becomes visible, it scrolls the chat to the end.
     * This is useful for ensuring the latest messages are visible when the keyboard is opened.
     */
    async keyboardVisibility() {
      if (this.keyboardVisibility) {
        this.scrollToEnd();
      }
    },

    /**
     * Watcher for the hasPreloader computed property.
     * Clears the forceHidePreloaderTimeout and sets the forceHidePreloader flag based on the hasPreloader value.
     * If hasPreloader is true, it scrolls to the end of the chat.
     * If hasPreloader is false, it sets a timeout to hide the preloader after 2 seconds.
     */
    hasPreloader() {
      clearTimeout(this.forceHidePreloaderTimeout);

      if (this.hasPreloader) {
        this.forceHidePreloader = false;

        this.scrollToEnd();
      } else {
        this.forceHidePreloaderTimeout = setTimeout(() => {
          this.forceHidePreloader = true;
        }, 2000);
      }
    },
  },

  created() {
    this.btnEvents = {
      [`${this.renderer?.a2u?.getPlatform?.() === "web" ? 'mousedown' : 'touchstart'}`]: this.sendRawMessage,
    };
  },

  /**
   * On mounted hook
   */
  async mounted() {
    // Watch the channel property
    this.$watch('channel', async () => {
      if (this.channel) await this.initChat();
    }, {immediate: true});
  },

  async beforeUnmount() {
    // If the chat diagram is enabled
    if (this.withChatDiagram) {
      // Unsubscribe from the chat completion event
      this.renderer.a2u.processManager.eventEmitter.removeListener(`chat:complete.${this.channel}`, this.chatCompleteListener);

      // Unsubscribe from new messages in the chat
      this.renderer.a2u.processManager.eventEmitter.removeListener(`chat:new-message.${this.channel}`, this.chatNewMessageListener);
    }

    // Unregister resize observer
    if (this.containerResizeObserver) {
      this.containerResizeObserver.disconnect();
    }
  }
}

</script>

<style lang="scss">
.chat-card {
  width: 100%;
  background: transparent !important;

  .attachment {
    .q-message-text {
      width: 250px;
    }
  }
}

.chat-message {
  .q-message-text {
    background: var(--message-bg-color);
    color: var(--message-bg-color);

    .q-message-text-content {
      color: var(--message-text-color);

      p:only-child {
        margin: 0;
      }
    }
  }

  .q-message-container {
    & > div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    &.reverse > div {
      align-items: flex-end;
    }
  }
}

.chat-field {
  .q-field__control {
    align-items: flex-end;
    background: var(--chat-control-bg-color) !important;

    &:after {
      display: none !important;
    }

    .q-field__native {
      max-height: 180px;
    }

    .q-field__native, .q-field__label {
      color: var(--chat-control-text-color) !important;
    }
  }
}

.opacity-0 {
  opacity: 0;
}

.btn-previews-messages {
  background: transparent;
  border: none;
  color: currentColor;
  cursor: pointer;
  font-size: 14px;
  padding: 6px 0;
  text-align: center;
  margin-bottom: 15px;
}

.chat-card {
  .chat-opt-btn {
    border: 1px solid currentColor;
    height: 36px;
    min-width: 120px;
    font-size: 12.6px;
  }
}
</style>
